import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { supabase } from "../../../lib/supabase";
import BetaDashboard from "./BetaDashboard";
import AvatarUploader from "./AvatarUploader";
import SettingsTab from "./SettingsTab";

const LOCKED_FEATURES = [
  { icon: "ri-bar-chart-2-line", label: "Performance Dashboard", desc: "Live daily stats & profit tracking" },
  { icon: "ri-line-chart-line", label: "ROI Analytics", desc: "Track returns and trends over time" },
  { icon: "ri-team-line", label: "Multi-User Access", desc: "Invite your team and collaborate" },
  { icon: "ri-upload-cloud-2-line", label: "Bulk Flip Import", desc: "Import hundreds of flips at once" },
  { icon: "ri-file-excel-2-line", label: "CSV Export", desc: "Export your full flip history" },
  { icon: "ri-customer-service-2-line", label: "Priority Support", desc: "Dedicated support channel" },
];

const NUDGE_DISMISSED_KEY = "trackly_upgrade_nudge_dismissed";

// ─── Analytics helper ──────────────────────────────────────────────────────────
async function trackEvent(
  userId: string | undefined,
  eventName: string,
  properties: Record<string, unknown> = {}
) {
  if (!userId) return;
  try {
    await supabase.from("analytics_events").insert({
      user_id: userId,
      event_name: eventName,
      properties,
    });
  } catch {
    // silent — never let tracking break the UI
  }
}

// ─── Upgrade Nudge Banner ──────────────────────────────────────────────────────
function UpgradeNudgeBanner() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(NUDGE_DISMISSED_KEY);
    if (!dismissed) setVisible(true);
  }, []);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(NUDGE_DISMISSED_KEY, "1");
    setVisible(false);
    trackEvent(user?.id, "nudge_banner_dismissed", { source: "upgrade_nudge_banner" });
  }, [user?.id]);

  const handleSeePlans = useCallback(() => {
    trackEvent(user?.id, "nudge_see_plans_clicked", { source: "upgrade_nudge_banner" });
  }, [user?.id]);

  if (!visible) return null;

  return (
    <div
      className={`mb-6 rounded-2xl border overflow-hidden relative ${
        isDark
          ? "bg-[#0f0d08] border-amber-500/25"
          : "bg-amber-50/70 border-amber-200/70"
      }`}
    >
      {/* Left accent bar */}
      <div className="absolute left-0 inset-y-0 w-1 rounded-l-2xl bg-gradient-to-b from-amber-400 to-amber-600" />

      {/* Dismiss — top right corner */}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss upgrade banner"
        className={`absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-colors duration-150 ${
          isDark ? "text-[#444] hover:text-[#888] hover:bg-white/6" : "text-[#bbb] hover:text-[#777] hover:bg-black/5"
        }`}
      >
        <i className="ri-close-line text-sm" />
      </button>

      {/* Content */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 pl-6 pr-10">
        {/* Icon + Copy row */}
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 mt-0.5 ${
            isDark ? "bg-amber-500/15" : "bg-amber-100"
          }`}>
            <i className={`ri-vip-crown-fill text-base ${isDark ? "text-amber-400" : "text-amber-600"}`} />
          </div>
          <div className="min-w-0">
            <p className={`font-inter text-sm font-semibold mb-0.5 ${isDark ? "text-white" : "text-[#111]"}`}>
              Upgrade to Pro and unlock everything
            </p>
            <p className={`font-inter text-xs leading-relaxed ${isDark ? "text-[#666]" : "text-[#888]"}`}>
              Performance analytics, CSV export, multi-user access and more — starting at $10/month.
            </p>
          </div>
        </div>

        {/* CTA */}
        <a
          href="/product#pricing-plans"
          onClick={handleSeePlans}
          className={`flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-inter text-xs font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 self-start sm:self-auto ${
            isDark
              ? "bg-amber-500 text-black hover:bg-amber-400"
              : "bg-amber-500 text-white hover:bg-amber-600"
          }`}
        >
          <i className="ri-arrow-right-up-line text-xs" />
          See plans
        </a>
      </div>
    </div>
  );
}

type Tab = "overview" | "beta" | "refer" | "settings";

export default function DashboardContent() {
  const { user, profile } = useAuth();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState<Tab>("overview");

  const isPro = profile?.plan === "pro";
  const displayName = profile?.display_name || user?.email?.split("@")[0] || "there";
  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const billingLabel =
    profile?.billing_period === "monthly"
      ? "$9 / month"
      : profile?.billing_period === "lifetime"
      ? "$200 — Lifetime"
      : "Free";

  return (
    <div className="min-h-screen pt-24 pb-16 px-8 lg:px-16">
      <div className="max-w-5xl mx-auto">

        {/* Welcome Header */}
        <div className={`mb-8 flex items-center gap-5 p-6 rounded-2xl border transition-all duration-300 ${
          isPro
            ? isDark
              ? "bg-[#0d0d0d] border-amber-500/20"
              : "bg-amber-50/40 border-amber-200/60"
            : isDark
              ? "bg-[#0d0d0d] border-white/6"
              : "bg-[#f8f8f8] border-black/6"
        }`}>

          {/* Avatar with glow when Pro */}
          <div className="relative flex-shrink-0">
            {isPro && (
              <div className="absolute inset-0 rounded-full blur-xl opacity-25 bg-amber-400 scale-110 pointer-events-none" />
            )}
            <AvatarUploader />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <h1 className={`font-syne font-bold text-3xl lg:text-4xl ${isDark ? "text-white" : "text-[#111]"}`}>
                Welcome, {displayName}
              </h1>
              {isPro && (
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-inter font-semibold text-xs border whitespace-nowrap ${
                  isDark
                    ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                    : "bg-amber-100 text-amber-700 border-amber-300/60"
                }`}>
                  <i className="ri-vip-crown-fill text-[11px]" />
                  PRO
                  {profile?.billing_period && (
                    <span className={`ml-0.5 font-normal ${isDark ? "text-amber-500/70" : "text-amber-600/70"}`}>
                      · {profile.billing_period === "lifetime" ? "Lifetime" : "Monthly"}
                    </span>
                  )}
                </span>
              )}
            </div>
            <p className={`font-inter text-sm ${isDark ? "text-[#555]" : "text-[#888]"}`}>
              {user?.email}
            </p>
            {isPro && (
              <p className={`font-inter text-xs mt-1 ${isDark ? "text-amber-500/50" : "text-amber-600/60"}`}>
                <i className="ri-shield-check-line mr-1" />
                Pro membership active
              </p>
            )}
          </div>
        </div>

        {/* Upgrade nudge for free users */}
        {!isPro && <UpgradeNudgeBanner />}

        {/* Top Tab Navigation */}
        <div className={`flex items-center gap-1 mb-8 border-b ${isDark ? "border-white/6" : "border-black/6"}`}>
          {/* Overview Tab */}
          <button
            type="button"
            onClick={() => setActiveTab("overview")}
            className={`flex items-center gap-2 px-5 py-3 font-inter text-sm font-medium cursor-pointer transition-colors duration-150 whitespace-nowrap border-b-2 -mb-px ${
              activeTab === "overview"
                ? isDark ? "border-white text-white" : "border-[#111] text-[#111]"
                : isDark ? "border-transparent text-[#555] hover:text-white/70" : "border-transparent text-[#bbb] hover:text-[#555]"
            }`}
          >
            <i className="ri-home-3-line text-[13px]" />
            Overview
          </button>

          {/* Beta Dashboard Tab */}
          <button
            type="button"
            onClick={() => setActiveTab("beta")}
            className={`flex items-center gap-2 px-5 py-3 font-inter text-sm font-medium cursor-pointer transition-colors duration-150 whitespace-nowrap border-b-2 -mb-px ${
              activeTab === "beta"
                ? isDark ? "border-white text-white" : "border-[#111] text-[#111]"
                : isDark ? "border-transparent text-[#555] hover:text-white/70" : "border-transparent text-[#bbb] hover:text-[#555]"
            }`}
          >
            <i className="ri-dashboard-3-line text-[13px]" />
            Beta Dashboard
            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${isDark ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-600"}`}>
              BETA
            </span>
          </button>

          {/* Refer a Friend Tab */}
          <button
            type="button"
            onClick={() => setActiveTab("refer")}
            className={`flex items-center gap-2 px-5 py-3 font-inter text-sm font-medium cursor-pointer transition-colors duration-150 whitespace-nowrap border-b-2 -mb-px ${
              activeTab === "refer"
                ? isDark ? "border-white text-white" : "border-[#111] text-[#111]"
                : isDark ? "border-transparent text-[#555] hover:text-white/70" : "border-transparent text-[#bbb] hover:text-[#555]"
            }`}
          >
            <i className="ri-gift-2-line text-[13px]" />
            Refer a Friend
          </button>

          {/* Settings Tab */}
          <button
            type="button"
            onClick={() => setActiveTab("settings")}
            className={`flex items-center gap-2 px-5 py-3 font-inter text-sm font-medium cursor-pointer transition-colors duration-150 whitespace-nowrap border-b-2 -mb-px ${
              activeTab === "settings"
                ? isDark ? "border-white text-white" : "border-[#111] text-[#111]"
                : isDark ? "border-transparent text-[#555] hover:text-white/70" : "border-transparent text-[#bbb] hover:text-[#555]"
            }`}
          >
            <i className="ri-settings-3-line text-[13px]" />
            Settings
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && <OverviewTab isPro={isPro} memberSince={memberSince} billingLabel={billingLabel} displayName={displayName} />}
        {activeTab === "beta" && <BetaDashboard />}
        {activeTab === "refer" && <ReferTab />}
        {activeTab === "settings" && <SettingsTab />}

        {/* Sign out */}
        <div className="mt-10 pt-6 border-t flex items-center justify-end" style={{ borderColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)" }}>
          <SignOutButton />
        </div>
      </div>
    </div>
  );
}

// ─── Overview Tab ──────────────────────────────────────────────────────────────
interface OverviewTabProps {
  isPro: boolean;
  memberSince: string;
  billingLabel: string;
  displayName: string;
}

function OverviewTab({ isPro, memberSince, billingLabel, displayName }: OverviewTabProps) {
  const { isDark } = useTheme();
  const { user } = useAuth();

  const handleViewPricing = useCallback(() => {
    trackEvent(user?.id, "nudge_see_plans_clicked", { source: "overview_bottom_cta" });
  }, [user?.id]);

  return (
    <div>
      {/* Plan Status Card */}
      <div className={`rounded-2xl p-6 mb-8 border ${isDark ? "bg-[#0d0d0d] border-white/6" : "bg-[#f8f8f8] border-black/6"}`}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className={`font-inter text-xs uppercase tracking-widest mb-1.5 ${isDark ? "text-[#555]" : "text-[#999]"}`}>
              Membership Plan
            </p>
            <div className="flex items-center gap-2.5 mb-0.5">
              <span className={`font-syne font-bold text-xl ${isDark ? "text-white" : "text-[#111]"}`}>
                {isPro ? "Pro" : "Early Access"}
              </span>
              {isPro && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold font-inter bg-amber-500/15 text-amber-500 border border-amber-500/25">
                  <i className="ri-vip-crown-fill text-[8px]" />
                  PRO
                </span>
              )}
            </div>
            <p className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#999]"}`}>
              {displayName} · Member since {memberSince}
            </p>
          </div>
          <div className="text-right">
            <p className={`font-inter text-xs uppercase tracking-widest mb-1 ${isDark ? "text-[#555]" : "text-[#999]"}`}>
              Billing
            </p>
            <p className={`font-syne font-semibold text-lg ${isDark ? "text-white" : "text-[#111]"}`}>
              {billingLabel}
            </p>
          </div>
        </div>
        <div className={`mt-5 pt-5 border-t flex items-center gap-3 ${isDark ? "border-white/5" : "border-black/5"}`}>
          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
          <p className={`font-inter text-sm ${isDark ? "text-[#666]" : "text-[#777]"}`}>
            {isPro
              ? "Your Pro membership is reserved and will activate automatically when Trackly launches into early access."
              : "Your early access slot is locked in. We\u2019ll notify you the moment we launch."}
          </p>
        </div>
      </div>

      {/* Features Grid (Locked) */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-5">
          <h2 className={`font-syne font-semibold text-lg ${isDark ? "text-white" : "text-[#111]"}`}>
            Your Features
          </h2>
          <span className={`font-inter text-xs px-2.5 py-1 rounded-full border ${isDark ? "text-[#555] border-white/8 bg-white/3" : "text-[#999] border-black/8 bg-black/3"}`}>
            Activates at launch
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {LOCKED_FEATURES.map((feature) => (
            <div
              key={feature.label}
              className={`rounded-xl p-4 border flex items-start gap-3 ${isDark ? "bg-[#0a0a0a] border-white/5" : "bg-[#fafafa] border-black/5"}`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${isDark ? "bg-white/5" : "bg-black/5"}`}>
                <i className={`${feature.icon} text-sm ${isDark ? "text-[#444]" : "text-[#bbb]"}`} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <p className={`font-inter text-sm font-medium ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>
                    {feature.label}
                  </p>
                  <i className={`ri-lock-2-line text-xs flex-shrink-0 ${isDark ? "text-[#333]" : "text-[#ccc]"}`} />
                </div>
                <p className={`font-inter text-xs leading-relaxed ${isDark ? "text-[#3a3a3a]" : "text-[#ccc]"}`}>
                  {feature.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Upgrade prompt for free users */}
      {!isPro && (
        <div className={`mt-8 rounded-2xl p-6 border flex items-center justify-between flex-wrap gap-4 ${isDark ? "bg-[#0d0d0d] border-white/6" : "bg-[#f8f8f8] border-black/6"}`}>
          <div>
            <p className={`font-syne font-semibold text-base mb-1 ${isDark ? "text-white" : "text-[#111]"}`}>
              Unlock everything with Pro
            </p>
            <p className={`font-inter text-sm ${isDark ? "text-[#555]" : "text-[#888]"}`}>
              Starting at $10/month or $200 for lifetime access.
            </p>
          </div>
          <a
            href="/product#pricing-plans"
            onClick={handleViewPricing}
            className={`font-inter text-sm font-medium px-5 py-2.5 rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 ${isDark ? "bg-white text-black hover:bg-[#e0e0e0]" : "bg-[#111] text-white hover:bg-[#333]"}`}
          >
            View Pricing
          </a>
        </div>
      )}
    </div>
  );
}

function SignOutButton() {
  const { signOut } = useAuth();
  const { isDark } = useTheme();
  return (
    <button
      type="button"
      onClick={signOut}
      className={`font-inter text-xs cursor-pointer transition-colors duration-200 ${isDark ? "text-[#444] hover:text-[#888]" : "text-[#ccc] hover:text-[#888]"}`}
    >
      <i className="ri-logout-box-r-line mr-1.5" />
      Sign out
    </button>
  );
}

// ─── Refer Tab ─────────────────────────────────────────────────────────────────
function ReferTab() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const referralCode = user?.id ? `TRK-${user.id.slice(0, 8).toUpperCase()}` : "TRK-XXXXXXXX";
  const referralUrl = `${window.location.origin}/?ref=${referralCode}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback silent
    }
  };

  const shareLinks = [
    {
      icon: "ri-twitter-x-line",
      label: "Twitter / X",
      href: `https://x.com/intent/tweet?text=I%27m%20using%20Trackly%20to%20track%20my%20sneaker%20%26%20streetwear%20flips.%20Get%20early%20access%20%E2%86%92%20${encodeURIComponent(referralUrl)}`,
    },
    {
      icon: "ri-whatsapp-line",
      label: "WhatsApp",
      href: `https://wa.me/?text=${encodeURIComponent(`Check out Trackly — the best tool for tracking your resell flips. Get early access: ${referralUrl}`)}`,
    },
    {
      icon: "ri-mail-send-line",
      label: "Email",
      href: `mailto:?subject=You%20need%20to%20try%20Trackly&body=${encodeURIComponent(`Hey! I've been using Trackly to track my sneaker and streetwear flips. You should grab early access here: ${referralUrl}`)}`,
    },
  ];

  return (
    <div className="max-w-xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${isDark ? "bg-white/6" : "bg-black/5"}`}>
          <i className={`ri-gift-2-line text-xl ${isDark ? "text-white/60" : "text-[#555]"}`} />
        </div>
        <div>
          <h2 className={`font-syne font-bold text-xl mb-0.5 ${isDark ? "text-white" : "text-[#111]"}`}>
            Refer a Friend
          </h2>
          <p className={`font-inter text-sm ${isDark ? "text-[#555]" : "text-[#888]"}`}>
            Share Trackly and help more flippers lock in early access.
          </p>
        </div>
      </div>

      {/* Referral URL row */}
      <div className={`rounded-xl border p-1 flex items-center gap-2 mb-4 ${isDark ? "bg-white/4 border-white/8" : "bg-[#f8f8f8] border-black/8"}`}>
        <div className="flex-1 overflow-hidden px-3 py-2">
          <p className={`font-inter text-xs truncate ${isDark ? "text-[#888]" : "text-[#666]"}`}>
            {referralUrl}
          </p>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg font-inter text-xs font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
            copied
              ? isDark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-50 text-emerald-600"
              : isDark ? "bg-white text-black hover:bg-[#e0e0e0]" : "bg-[#111] text-white hover:bg-[#333]"
          }`}
        >
          <i className={`${copied ? "ri-checkbox-circle-line" : "ri-file-copy-line"} text-xs`} />
          {copied ? "Copied!" : "Copy Link"}
        </button>
      </div>

      {/* Referral code card */}
      <div className={`flex items-center justify-between px-5 py-4 rounded-xl border mb-8 ${isDark ? "border-white/6 bg-white/3" : "border-black/6 bg-black/3"}`}>
        <div>
          <p className={`font-inter text-[10px] uppercase tracking-widest mb-1 ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>
            Your referral code
          </p>
          <p className={`font-syne font-semibold text-lg tracking-wide ${isDark ? "text-white" : "text-[#111]"}`}>
            {referralCode}
          </p>
        </div>
        <i className={`ri-vip-crown-line text-2xl ${isDark ? "text-amber-500/60" : "text-amber-500"}`} />
      </div>

      {/* Share buttons */}
      <p className={`font-inter text-xs uppercase tracking-widest mb-3 ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>
        Share via
      </p>
      <div className="grid grid-cols-3 gap-3">
        {shareLinks.map((btn) => (
          <a
            key={btn.label}
            href={btn.href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className={`flex flex-col items-center gap-2 py-4 rounded-xl border text-center cursor-pointer transition-colors duration-150 ${
              isDark
                ? "border-white/6 bg-white/3 hover:bg-white/6 text-white/60 hover:text-white/80"
                : "border-black/6 bg-black/3 hover:bg-black/5 text-[#666] hover:text-[#333]"
            }`}
          >
            <i className={`${btn.icon} text-lg`} />
            <span className="font-inter text-xs">{btn.label}</span>
          </a>
        ))}
      </div>

      {/* Bottom note */}
      <div className={`mt-8 rounded-xl border px-5 py-4 flex items-start gap-3 ${isDark ? "border-white/5 bg-white/2" : "border-black/5 bg-black/2"}`}>
        <i className={`ri-information-line text-sm mt-px flex-shrink-0 ${isDark ? "text-[#444]" : "text-[#ccc]"}`} />
        <p className={`font-inter text-xs leading-relaxed ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>
          Referral rewards will be tracked and credited automatically when Trackly launches. More details coming soon.
        </p>
      </div>
    </div>
  );
}
